import { useMemo } from "react";
import PencilIcon from "shared/components/icons/pencil/PencilIcon";
import TrashOutlineIcon from "shared/components/icons/trash/TrashOutlineIcon";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { formatUsdString } from "shared/helpers/invoice/formatUsdString";
import { type ProductView } from "shared/mappers/database/accounting/product/product";
import * as Yup from "yup";
import { useHubCheckoutContext } from "hub/src/pages/checkout/context/HubCheckoutContext";
import { useHubCheckoutInvoiceItemContext } from "hub/src/pages/checkout/context/HubCheckoutInvoiceItemContext";
import { HubCheckoutDiscountCode } from "hub/src/pages/checkout/discount-code/HubCheckoutDiscountCode";
import HubCheckoutDiscountCodeModal from "hub/src/pages/checkout/discount-code/HubCheckoutDiscountCodeModal";
import { type HubCheckoutOptionalGridProduct } from "hub/src/pages/checkout/optional-products/HubCheckoutOptionalProductItem";
import { HubCheckoutDeleteInvoiceItemModal } from "hub/src/pages/checkout/summary/HubCheckoutDeleteInvoiceItemModal";

type HubCheckoutInvoiceItemDetailsFormValue = {
  amount?: string;
  quantity?: number;
  notes?: string;
};

type OptionalProductInvoiceItemAdditionalInfo = {
  contentZone?: HubCheckoutOptionalGridProduct;
  notes?: string;
};

export const HubCheckoutInvoiceItemDetails = () => {
  const { invoiceItem, index, setDeleteInvoiceItemModalOpen } =
    useHubCheckoutInvoiceItemContext();
  const customSession = useCustomSession();

  const isAdmin = customSession.adminMode;
  const {
    invoice,
    invoiceIsLoading,
    handleUpdateInvoice,
    optionalProductZone,
    editingInvoiceItem,
    setEditingInvoiceItem,
  } = useHubCheckoutContext();

  const invoiceItemAdditionalInfo =
    invoiceItem?.additionalInfo as OptionalProductInvoiceItemAdditionalInfo;

  const editState =
    editingInvoiceItem?.invoiceItemId === invoiceItem.invoiceItemId;

  const contentPageProducts = optionalProductZone?.data?.products ?? [];

  const editableInvoiceItem = useMemo(() => {
    if (!invoiceItem.product.optionalProduct) {
      return undefined;
    }
    return contentPageProducts.find(
      (p: ProductView) => p.productId === invoiceItem.product.productId,
    );
  }, [
    invoiceItem.product.optionalProduct,
    invoiceItem.product.productId,
    contentPageProducts,
  ]);

  const canDeleteInvoiceItem =
    (invoiceItem.product.optionalProduct?.allowRemove &&
      editableInvoiceItem?.allowRemove) ||
    isAdmin;

  const canChangeTotal =
    invoiceItem.product.optionalProduct?.allowChangeTotal || isAdmin;

  const canAddNotes =
    invoiceItem.product.optionalProduct?.allowNotes || isAdmin;

  const invoiceItemDetailsYupValidation = Yup.object({
    amount:
      editState && !invoiceItemAdditionalInfo?.contentZone!.totalOptions
        ? Yup.string()
            .min(0.01, "Minimum amount is $0.01")
            .required("Amount is required")
        : Yup.string().optional(),
    notes: Yup.string().optional(),
  });

  return (
    <>
      <HubCheckoutDiscountCodeModal />

      <fieldset disabled={invoiceIsLoading}>
        <PillarForm<HubCheckoutInvoiceItemDetailsFormValue>
          handleSubmit={async (
            formValues: HubCheckoutInvoiceItemDetailsFormValue,
          ) => {
            await handleUpdateInvoice({
              invoiceItems: [
                {
                  productId: invoiceItem.product.productId,
                  invoiceItemId: invoiceItem.invoiceItemId,
                  total: Number(formValues.amount) ?? invoiceItem.total,
                  additionalInfo: {
                    notes: formValues.notes ?? "",
                    contentZone: {
                      ...editableInvoiceItem,
                    },
                  },
                },
              ],
            });
            setEditingInvoiceItem(null);
          }}
          overrideInitialValues={{
            amount: editState ? invoiceItem.total?.toFixed(2) : "",
            notes: invoiceItemAdditionalInfo?.notes ?? "",
          }}
          yupValidation={invoiceItemDetailsYupValidation}
        >
          <HubCheckoutDeleteInvoiceItemModal />
          <div
            key={`invoice-item-${invoiceItem.invoiceItemId}`}
            data-testid={`order-summary-invoice-item-${invoiceItem.invoiceItemId}`}
            className={
              "py-1 pl-1 items-start rounded-lg " +
              (index % 2 == 0 ? "bg-neutral-light" : "bg-neutral-light-550")
            }
          >
            <div className="flex flex-col space-y-0.5">
              <div className="flex items-center justify-between">
                <p className="text-detailblock-title">
                  {invoiceItem?.productName}
                </p>

                {editableInvoiceItem && !editState && (
                  <div className="flex gap-x-1 pr-2">
                    <PillarForm.Button
                      className="button-regular-neutral-filled w-fit whitespace-nowrap"
                      testid={`order-summary-edit-invoice-item-button-${invoiceItem.invoiceItemId}`}
                      onClick={() => {
                        setEditingInvoiceItem(invoiceItem);
                      }}
                    >
                      <PencilIcon className="w-1.5 h-1.5" />
                    </PillarForm.Button>

                    {canDeleteInvoiceItem &&
                      invoice?.invoiceItems &&
                      invoice.invoiceItems.length > 1 && (
                        <PillarForm.Button
                          className="button-small-destructive-filled"
                          testid={`order-summary-remove-invoice-item-button-${invoiceItem.invoiceItemId}`}
                          onClick={() =>
                            setDeleteInvoiceItemModalOpen(invoiceItem)
                          }
                        >
                          <TrashOutlineIcon className="w-1.5 h-1.5 mx-1" />
                        </PillarForm.Button>
                      )}
                  </div>
                )}
              </div>
              {!editState && invoiceItemAdditionalInfo?.notes && (
                <p className="text-detailblock-subtle text-sm">
                  {invoiceItemAdditionalInfo.contentZone!.notesInstructions ??
                    "Notes"}
                  : <strong> {invoiceItemAdditionalInfo.notes}</strong>
                </p>
              )}
              {editState &&
                canAddNotes &&
                invoiceItemAdditionalInfo?.contentZone!.notesEnabled && (
                  <PillarForm.TextAreaInput
                    name="notes"
                    className="text-xs w-full"
                    testid={`order-summary-edit-invoice-item-notes-${invoiceItem.invoiceItemId}`}
                    placeholder="Add notes or instructions here"
                    label={
                      invoiceItemAdditionalInfo.contentZone!
                        .notesInstructions || "Notes:"
                    }
                  />
                )}

              {editState && canChangeTotal && (
                <>
                  {invoiceItemAdditionalInfo?.contentZone!.totalOptions &&
                  invoiceItemAdditionalInfo?.contentZone!.totalOptions?.length >
                    1 ? (
                    <div className="pt-1">
                      <PillarForm.SelectComponent
                        name="amount"
                        label="Amount"
                        placeholder="Select Amount"
                        queryCallback={async ({ text }) => {
                          const results = (
                            invoiceItemAdditionalInfo?.contentZone
                              ?.totalOptions ?? []
                          ).filter((option) =>
                            formatUsdString(option)
                              .toString()
                              .includes((text || "").toString().toLowerCase()),
                          );
                          return {
                            results,
                            totalResults: 1,
                            page: 1,
                            pageSize: results.length,
                          };
                        }}
                        labelCallback={(option) => `$${option}`}
                        valueCallback={(option) => option}
                      />
                    </div>
                  ) : (
                    <PillarForm.NumberInput
                      name="amount"
                      placeholder="$0.00"
                      testid={`order-summary-edit-invoice-item-amount-${invoiceItem.invoiceItemId}`}
                      className="text-xs resize-none w-full"
                      label="Amount"
                    />
                  )}
                </>
              )}

              <div className="flex flex-wrap gap-x-1">
                <div>
                  <span className="text-detailblock-base">Quantity:&nbsp;</span>
                  <span className="text-detailblock-subtle">
                    {invoiceItem?.quantity}
                  </span>
                </div>
                <div>
                  <span className="text-detailblock-base">
                    {editState ? "Current Price" : "Total Price"}
                    :&nbsp;
                  </span>
                  <span className="text-detailblock-subtle">
                    {formatUsdString(invoiceItem.total ?? 0)}
                  </span>
                </div>
              </div>

              {editState && (
                <div className="pt-1">
                  <div className="flex gap-x-1">
                    <PillarForm.LoadingSubmitButton
                      className="button-small-success-filled w-fit whitespace-nowrap"
                      testid={`order-summary-save-invoice-item-button-${invoiceItem.invoiceItemId}`}
                    >
                      Apply Changes
                    </PillarForm.LoadingSubmitButton>
                    <PillarForm.Button
                      onClick={() => setEditingInvoiceItem(null)}
                      className="button-small-destructive-filled w-fit whitespace-nowrap"
                      testid="order-summary-cancel-edit-invoice-button"
                    >
                      Cancel
                    </PillarForm.Button>
                  </div>
                </div>
              )}
            </div>
            <HubCheckoutDiscountCode />
          </div>
        </PillarForm>
      </fieldset>
    </>
  );
};
